import React from 'react';
import type { GridStudent } from '../store/slices/journalSlice';
import type { JournalColumn } from '../utils/journalCell';
import { resolveCellState, findSubscriptionStart, subscriptionTintIndex } from '../utils/journalCell';
import { formatDateEuropean } from '../utils/dateUtils';
import './Journal.css';

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

function shortDate(ymd: string): string {
  return `${ymd.slice(8, 10)}.${ymd.slice(5, 7)}`;
}

function weekday(ymd: string): string {
  return WEEKDAYS[new Date(ymd + 'T00:00:00').getDay()];
}

const CELL_GLYPHS: Record<string, string> = {
  attended: '✓',
  absent: 'A',
  ill: 'S',
  vacation: 'V',
  frozen: '❄',
  ghost: '',
  out: '',
  canceled: '',
};

interface JournalGridProps {
  columns: JournalColumn[];
  students: GridStudent[];
  today: string;
  /** Cells with an in-flight mutation, keyed `${date}:${studentId}` */
  pendingCells: Set<string>;
  onCellClick: (student: GridStudent, column: JournalColumn, rect: DOMRect) => void;
  onHeaderClick: (column: JournalColumn, rect: DOMRect) => void;
  scrollRef: React.RefObject<HTMLDivElement | null>;
}

const JournalGrid: React.FC<JournalGridProps> = ({
  columns,
  students,
  today,
  pendingCells,
  onCellClick,
  onHeaderClick,
  scrollRef,
}) => {
  const displayName = (s: GridStudent) =>
    `${s.firstName || ''} ${s.lastName || ''}${s.surname ? ' ' + s.surname : ''}`.trim();

  return (
    <div className="journal-table-wrap" ref={scrollRef}>
      <table className="journal-table">
        <thead>
          <tr>
            <th className="journal-corner">Student</th>
            {columns.map((col) => {
              if (col.kind === 'teacherVacation') {
                return (
                  <th
                    key={`tv-${col.vacation.id}`}
                    className="journal-th journal-th--teacher-vacation"
                    title={`Teacher vacation ${formatDateEuropean(col.vacation.dateFrom)} – ${formatDateEuropean(col.vacation.dateTo)}`}
                  >
                    <span className="journal-th__vertical">
                      {shortDate(col.vacation.dateFrom)} – {shortDate(col.vacation.dateTo)} · vacation
                    </span>
                  </th>
                );
              }
              if (col.kind === 'groupEnd') {
                return (
                  <th
                    key="group-end"
                    className="journal-th journal-th--group-end"
                    title={`Group ends ${formatDateEuropean(col.date)}`}
                  >
                    <span className="journal-th__vertical">{shortDate(col.date)} · group end</span>
                  </th>
                );
              }
              const isGhost = col.kind === 'ghost';
              const isCanceled = col.kind === 'lesson' && col.lesson.canceled;
              const title =
                col.kind === 'lesson'
                  ? `${formatDateEuropean(col.date)}${col.lesson.topic ? ` — ${col.lesson.topic}` : ''}${isCanceled ? ' (canceled)' : ''}`
                  : `${formatDateEuropean(col.date)} — planned, click to create the lesson`;
              return (
                <th
                  key={col.date}
                  data-date={col.date}
                  className={[
                    'journal-th',
                    isGhost ? 'journal-th--ghost' : '',
                    isCanceled ? 'journal-th--canceled' : '',
                    col.date === today ? 'journal-th--today' : '',
                  ].join(' ')}
                  title={title}
                  onClick={(e) => onHeaderClick(col, e.currentTarget.getBoundingClientRect())}
                >
                  <span className="journal-th__date">{shortDate(col.date)}</span>
                  <span className="journal-th__weekday">{weekday(col.date)}</span>
                  {col.kind === 'lesson' && col.lesson.topic && !isCanceled && (
                    <span className="journal-th__topic-dot" />
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {students.map((student) => (
            <tr key={student.id} className={student.isFormer ? 'journal-row--former' : ''}>
              <th className="journal-student">
                <span className="journal-student__name">{displayName(student)}</span>
                {student.isFormer && <span className="journal-student__badge">left</span>}
              </th>
              {columns.map((col) => {
                if (col.kind === 'teacherVacation') {
                  return <td key={`tv-${col.vacation.id}`} className="journal-cell journal-cell--teacher-vacation" />;
                }
                if (col.kind === 'groupEnd') {
                  return <td key="group-end" className="journal-cell journal-cell--group-end" />;
                }
                const { state } = resolveCellState(student, col);
                const subStart = state !== 'out' ? findSubscriptionStart(student, col.date) : undefined;
                const tint = state !== 'out' ? subscriptionTintIndex(student, col.date) : null;
                const pending = pendingCells.has(`${col.date}:${student.id}`);
                const interactive = state !== 'out' && !pending;

                const titleParts: string[] = [formatDateEuropean(col.date)];
                if (state !== 'attended' && state !== 'ghost') titleParts.push(state);
                if (subStart) {
                  titleParts.push(
                    `New subscription: paid ${formatDateEuropean(subStart.paymentDate)} · ` +
                    `${subStart.lessonsCount} lessons · ${subStart.totalAmount} BYN · ` +
                    `until ${subStart.effectiveEndDate ? formatDateEuropean(subStart.effectiveEndDate) : '—'} · ` +
                    `${subStart.consumedCount}/${subStart.lessonsCount} used`
                  );
                }

                return (
                  <td
                    key={col.date}
                    className={[
                      'journal-cell',
                      `journal-cell--${state}`,
                      tint !== null ? `journal-cell--tint-${tint}` : '',
                      pending ? 'journal-cell--pending' : '',
                      col.date === today ? 'journal-cell--today' : '',
                    ].join(' ')}
                    title={titleParts.join(' · ')}
                    onClick={
                      interactive
                        ? (e) => onCellClick(student, col, e.currentTarget.getBoundingClientRect())
                        : undefined
                    }
                  >
                    {subStart && <span className="journal-cell__sub-start" />}
                    <span className="journal-cell__glyph">{CELL_GLYPHS[state]}</span>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default JournalGrid;
